import {
  docGroupHtml,
  invoiceSummaryListHtml,
  quoteSummaryListHtml,
} from "../components/docCards";
import {
  abnLookupFieldHtml,
  confirmAbnBeforeSave,
  wireAbnLookup,
} from "../components/abnLookup";
import { panelHeaderHtml, formActionsHtml } from "../components/formActions";
import { emptyStateHtml, showToast } from "../components/ui";
import { cache } from "../data/idb";
import { deleteCustomer, flushQueue, upsertCustomer } from "../data/sync";
import {
  addressFieldsHtml,
  addressSnippet,
  normalizeAbn,
  normalizeAuAddress,
  readAddressFromForm,
} from "../domain/address";
import {
  customerStats,
  invoicesForCustomer,
  quotesForCustomer,
} from "../domain/customers";
import {
  formatInvoiceDate,
  formatMoney,
} from "../domain/invoiceFormat";
import { esc } from "../lib/escape";
import { wireDeleteAction, wireFormScreen } from "../lib/formActions";
import { attachFormGuard, clearLeaveGuard, setGlobalSaveHandler } from "../lib/unsaved";
import { currentRouteTarget } from "../routes";
import { goAfterSave, goBack } from "../lib/navigation";
import { router } from "../router";
import type { AppContext } from "../types";
import { startCreateInvoiceForCustomer, wireInvoiceCardActions } from "./invoices";
import { wireQuoteCardActions } from "./quotes";

export async function renderCustomers(panel: HTMLElement, ctx: AppContext) {
  clearLeaveGuard();
  if (router.sub === "add" || router.sub === "edit") {
    return renderCustomerForm(panel, ctx);
  }
  if (router.sub === "view") {
    return renderCustomerProfile(panel, ctx);
  }
  const customers = await cache.getCustomers();
  const names = Object.keys(customers).sort();
  panel.innerHTML = `
    <div class="panel-header"><h2>Customers</h2><button type="button" class="btn small primary" id="add-customer">Add</button></div>
    ${
      names.length
        ? names.map((n) => customerCard(n, customers[n])).join("")
        : emptyStateHtml(
            "No customers yet",
            "Add a customer before creating an invoice.",
            "empty-add-customer",
            "Add customer"
          )
    }`;
  panel.querySelector("#add-customer")?.addEventListener("click", () =>
    router.navigate("customers", "add")
  );
  panel.querySelector("#empty-add-customer")?.addEventListener("click", () =>
    router.navigate("customers", "add")
  );
  panel.querySelectorAll("[data-view-customer]").forEach((btn) => {
    btn.addEventListener("click", () =>
      router.navigate("customers", "view", {
        name: (btn as HTMLElement).dataset.viewCustomer || "",
      })
    );
  });
}

function customerCard(name: string, record: Record<string, unknown>) {
  const snippet = addressSnippet(record);
  const notes = String(record.notes || "").trim();
  const notesHint =
    notes.length > 80 ? `${notes.slice(0, 80).trim()}…` : notes;
  return `<article class="card card-click" data-view-customer="${esc(name)}">
    <strong>${esc(name)}</strong>
    <div class="meta">${esc(String(record.email || "No email"))}</div>
    ${snippet ? `<div class="meta">${esc(snippet)}</div>` : ""}
    ${notesHint ? `<div class="meta meta-notes">${esc(notesHint)}</div>` : ""}
  </article>`;
}

async function renderCustomerProfile(panel: HTMLElement, ctx: AppContext) {
  const name = String(router.params.name || "").trim();
  const customers = await cache.getCustomers();
  const record = customers[name];
  if (!name || !record) {
    showToast("Customer not found.", "error");
    router.navigate("customers");
    return;
  }

  const [invoices, quotes, settings, businesses] = await Promise.all([
    cache.getInvoices(),
    cache.getQuotes(),
    cache.getSettings(),
    cache.getBusinesses(),
  ]);
  const stats = customerStats(invoices, name);
  const customerInvoices = invoicesForCustomer(invoices, name);
  const quotesEnabled = Boolean(settings.quotes_enabled);
  const customerQuotes = quotesEnabled ? quotesForCustomer(quotes, name) : [];
  const multiBiz = Object.keys(businesses).length > 1;
  const snippet = addressSnippet(record);
  const email = String(record.email || "").trim();
  const abn = String(record.abn || "").trim();
  const notes = String(record.notes || "").trim();

  const quotesHtml = quotesEnabled
    ? docGroupHtml(
        "Quotes",
        customerQuotes.length,
        quoteSummaryListHtml(customerQuotes, {
          multiBiz,
          invoices,
          showCustomer: false,
          showActions: true,
        }),
        {
          open: false,
          emptyHint: "No quotes for this customer yet.",
        }
      )
    : "";

  const invoicesHtml = docGroupHtml(
    "Invoices",
    customerInvoices.length,
    invoiceSummaryListHtml(customerInvoices, {
      settings,
      multiBiz,
      quotes,
      showCustomer: false,
      showActions: true,
    }),
    {
      open: true,
      emptyHint: "No invoices for this customer yet.",
    }
  );

  panel.innerHTML = `
    <div class="customer-profile">
      <div class="panel-header">
        <div class="panel-header-start">
          <button type="button" class="btn small ghost" id="back-customers" aria-label="Back to customers">←</button>
          <h2>${esc(name)}</h2>
        </div>
        <button type="button" class="btn small secondary" id="edit-customer">Edit</button>
      </div>

      <div class="preview-section">
        <div class="preview-row"><span class="preview-label">Email</span><strong>${esc(email || "—")}</strong></div>
        <div class="preview-row"><span class="preview-label">Address</span><strong>${esc(snippet || "—")}</strong></div>
        ${abn ? `<div class="preview-row"><span class="preview-label">ABN</span><strong>${esc(abn)}</strong></div>` : ""}
        ${notes ? `<div class="preview-row"><span class="preview-label">Notes</span><strong>${esc(notes)}</strong></div>` : ""}
      </div>

      <div class="preview-section customer-stats">
        <div class="preview-row"><span class="preview-label">Invoices</span><strong>${stats.invoiceCount}</strong></div>
        <div class="preview-row"><span class="preview-label">Outstanding</span><strong>${formatMoney(stats.outstandingIncGst)}</strong></div>
        <div class="preview-row"><span class="preview-label">Paid</span><strong>${formatMoney(stats.paidIncGst)}</strong></div>
        <div class="preview-row"><span class="preview-label">Last invoice</span><strong>${
          stats.lastInvoiceDate ? esc(formatInvoiceDate(stats.lastInvoiceDate)) : "—"
        }</strong></div>
      </div>

      <div class="btn-row stacked">
        <button type="button" class="btn primary" id="new-invoice-customer">New invoice</button>
      </div>

      ${invoicesHtml}
      ${quotesHtml}
    </div>`;

  panel.querySelector("#back-customers")?.addEventListener("click", () => {
    const cur = currentRouteTarget(router.tab, router.sub, router.params);
    goBack(cur);
  });
  panel.querySelector("#edit-customer")?.addEventListener("click", () =>
    router.navigate("customers", "edit", { name })
  );
  panel.querySelector("#new-invoice-customer")?.addEventListener("click", () =>
    startCreateInvoiceForCustomer(name)
  );

  const returnToCustomer = { tab: "customers", sub: "view", name };
  const refreshProfile = async () => {
    await renderCustomerProfile(panel, ctx);
  };

  wireInvoiceCardActions(panel, ctx, {
    customers,
    onRefresh: refreshProfile,
    openReturnTo: returnToCustomer,
  });
  if (quotesEnabled) {
    wireQuoteCardActions(panel, ctx, {
      customers,
      businesses,
      onRefresh: refreshProfile,
      openReturnTo: returnToCustomer,
    });
  }
}

async function renderCustomerForm(panel: HTMLElement, ctx: AppContext) {
  const editing = router.sub === "edit";
  const name = editing ? router.params.name || "" : "";
  const customers = await cache.getCustomers();
  const record = editing ? customers[name] || {} : {};
  panel.innerHTML = `
    <form id="customer-form" class="panel" novalidate>
      ${panelHeaderHtml({ title: editing ? "Edit customer" : "Add customer" })}
      ${abnLookupFieldHtml({
        name: "abn",
        id: "customer_abn",
        value: record.abn || "",
        label: "ABN",
        optionalMark: true,
      })}
      ${
        editing
          ? `<div class="field"><label>Name</label><input id="customer_name" value="${esc(name)}" disabled></div>`
          : `<div class="field"><label>Name</label><input name="name" id="customer_name" required></div>`
      }
      <div class="field"><label>Email</label><input name="email" type="email" value="${esc(record.email)}"></div>
      ${addressFieldsHtml("", record)}
      <div class="field"><label>Notes (to yourself)</label>
        <textarea name="notes" rows="2" placeholder="Not shown on invoices">${esc(record.notes || "")}</textarea></div>
      <p class="error-text" id="form-error" hidden></p>
      ${formActionsHtml({
        saveId: "save-btn",
        showDelete: editing,
        deleteId: "delete-btn",
        deleteLabel: "Delete",
      })}
    </form>`;

  const form = panel.querySelector("#customer-form") as HTMLFormElement;
  const guard = attachFormGuard(form);

  const abnLookup = wireAbnLookup({
    root: form,
    abnInput: form.querySelector("#customer_abn") as HTMLInputElement,
    nameInput: form.querySelector("#customer_name") as HTMLInputElement,
    stateSelect: form.querySelector('select[name="state"]') as HTMLSelectElement | null,
    postcodeInput: form.querySelector('input[name="postcode"]') as HTMLInputElement | null,
  });

  const goAfterEdit = (custName: string) =>
    router.navigate("customers", "view", { name: custName }, { skipPush: true });

  const runSave = async () => {
    const fd = new FormData(form);
    const custName = editing ? name : String(fd.get("name") || "").trim();
    if (!custName) throw new Error("Customer name is required.");
    const email = String(fd.get("email") || "").trim();
    const addr = normalizeAuAddress(readAddressFromForm(fd));
    const abn = fd.get("abn") ? normalizeAbn(String(fd.get("abn"))) : "";
    const notes = String(fd.get("notes") || "").trim();
    const abnOk = await confirmAbnBeforeSave({
      abnRaw: abn,
      form: {
        name: custName,
        state: addr.state,
        postcode: addr.postcode,
      },
      handle: abnLookup,
    });
    if (!abnOk) throw new Error("__abn_cancelled");
    const existing = editing ? (await cache.getCustomers())[custName] : null;
    const createdVia =
      (existing && typeof existing.created_via === "string" && existing.created_via) ||
      (editing ? undefined : "list");
    await upsertCustomer(custName, {
      email,
      ...addr,
      abn,
      notes,
      ...(createdVia ? { created_via: createdVia } : {}),
    });
    await flushQueue(ctx.onSyncStatus);
    return custName;
  };

  setGlobalSaveHandler(async () => {
    try {
      await runSave();
      return true;
    } catch {
      return false;
    }
  });

  wireFormScreen({
    panel,
    guard,
    errorEl: "#form-error",
    back: { button: "#back-btn" },
    save: {
      button: "#save-btn",
      onSave: async () => {
        try {
          await runSave();
        } catch (ex) {
          if (ex instanceof Error && ex.message === "__abn_cancelled") {
            throw new Error("Save cancelled.");
          }
          throw ex;
        }
      },
      afterSaveToast: "Customer saved.",
      onAfterSave: async () => {
        const custName = editing ? name : String((new FormData(form)).get("name") || "").trim();
        goAfterEdit(custName);
      },
    },
  });

  if (editing) {
    wireDeleteAction({
      panel,
      button: "#delete-btn",
      message: `Delete customer "${name}"?`,
      confirmLabel: "Delete",
      guard,
      onDelete: async () => {
        const invoices = await cache.getInvoices();
        const inUse = Object.values(invoices).some(
          (inv) => !inv.deleted_at && inv.customer_name === name
        );
        if (inUse) throw new Error("Cannot delete: this customer is used on an invoice.");
        await deleteCustomer(name);
        await flushQueue(ctx.onSyncStatus);
      },
      afterToast: "Customer deleted.",
      navigateTo: { tab: "customers", sub: null, params: {} },
    });
  }
}
